import { Command, CommandItem, CommandList, Popover, PopoverContent, PopoverTrigger, Tooltip } from '@ibsys/kit';
import { CheckIcon } from 'lucide-react';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EMPTY_RESULT, useUpdateExpertReportBlockManualRating } from '@/entities/expert-report';
import { cn } from '@/shared/lib';
import { TEXT_DIFF_ADDED_CLASS, TEXT_DIFF_REMOVED_CLASS } from '@/shared/ui';

type Props = {
  conversationId: number;
  expertReportId: number;
  blockId: number;
  /** Значение, полученное системой */
  value: string | null;
  /** Значение, выбранное пользователем; null — значение не выбиралось */
  manualValue: string | null;
  /** Справочник, из которого выбирается значение блока */
  possibleValues: string[];
};

/**
 * Правка значения блока по месту: справочник открывается в поповере, выбор сохраняется сразу.
 * Значение, совпавшее с полученным системой, снимает ручное.
 * Правленое значение показано сравнением: значение системы перечёркнуто, действующее подсвечено;
 * автор и время правки в заключении не показываются — они видны в разделе «Обратная связь».
 */
export const EvaluationBlockValueEditor: FC<Props> = ({
  conversationId,
  expertReportId,
  blockId,
  value,
  manualValue,
  possibleValues,
}) => {
  const { t } = useTranslation(['dialogs']);
  const [isOpen, setIsOpen] = useState(false);
  const { mutateAsync, isPending } = useUpdateExpertReportBlockManualRating();

  const isManual = manualValue !== null;
  const effectiveValue = manualValue ?? value;

  const handleSelect = useCallback(
    async (selected: string) => {
      setIsOpen(false);
      // Значение, совпавшее с полученным системой, ручным не считается
      const nextManual = selected === value ? null : selected;
      if (nextManual === manualValue) return;
      try {
        await mutateAsync({ conversationId, expertReportId, blockId, manualValue: nextManual });
      } catch {
        // Ошибку показал глобальный перехватчик запросов; действующее значение придёт с сервера
      }
    },
    [conversationId, expertReportId, blockId, value, manualValue, mutateAsync],
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={-1}
          aria-label={t('dialogs:expertReport.editValue')}
          className={cn(
            'shrink-0 whitespace-nowrap cursor-pointer hover:text-primary/80',
            isManual ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {isManual ? (
            <>
              <s className={cn(TEXT_DIFF_REMOVED_CLASS, 'ml-0 mr-1')}>{value}</s>
              <Tooltip tooltip={t('dialogs:expertReport.manualValueHint')}>
                <mark className={cn(TEXT_DIFF_ADDED_CLASS, 'mx-0 underline')}>{manualValue}</mark>
              </Tooltip>
            </>
          ) : (
            <span className="underline">{value ?? EMPTY_RESULT}</span>
          )}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1" align="start">
        <Command shouldFilter={false}>
          <CommandList>
            {possibleValues.map((option) => (
              <CommandItem key={option} value={option} disabled={isPending} onSelect={() => handleSelect(option)}>
                <CheckIcon className={cn('size-4', option === effectiveValue ? 'opacity-100' : 'opacity-0')} />
                {option}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
